import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { FiVideo } from 'react-icons/fi';
import BroadcastTreeSidebar from '../components/BroadcastTreeSidebar';
import {
  getLiveBroadcasts,
  getBroadcasts,
  getBroadcastFilterTree,
  getBroadcastById,
} from '../api/publicApi';
import useFetch from '../hooks/useFetch';
import { SkeletonBox } from '../components/Skeleton';
import { VideoPreview } from '../components/VideoPlayers';

const LIMIT = 16;

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function normaliseBroadcast(b) {
  // Decode &amp; in embed URLs stored by the backend
  const decodeAmp = (url) => url ? url.replace(/&amp;/g, '&') : null;
  return {
    id: b.id ?? b._id,
    _id: b.id ?? b._id,
    theme: b.titre ?? b.title ?? b.theme ?? '',
    intervenant: b.speaker_name ?? b.speaker ?? b.intervenant ?? '',
    lien_primaire: b.primary_link ?? b.url ?? b.lien_primaire ?? '',
    date: b.date_scheduled ?? b.publication_date ?? b.date ?? '',
    is_live: b.is_live ?? false,
    is_recording: b.is_recording ?? false,
    thumbnail: b.thumbnail_url ?? b.thumbnail ?? null,
    location: b.location ?? '',
    primary_platform: b.primary_platform ?? null,
    primary_video_id: b.primary_video_id ?? null,
    primary_embed_url: decodeAmp(b.primary_embed_url),
  };
}

// ── Shared sub-components ────────────────────────────────────────────────────

/** Speaker / location meta row below the player */
function BroadcastMeta({ broadcast }) {
  if (!broadcast) return null;
  const { intervenant, location, theme } = broadcast;
  if (!intervenant && !location && !theme) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 20,
      background: '#f7f8fa', borderRadius: 10,
      padding: '14px 20px', fontSize: 14, marginBottom: 8,
    }}>
      {intervenant && (
        <div><span style={{ fontWeight: 700, color: '#555' }}>🎙 Intervenants : </span>{intervenant}</div>
      )}
      {location && (
        <div><span style={{ fontWeight: 700, color: '#555' }}>📍 Lieu : </span>{location}</div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function DirectsPage() {
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [filters, setFilters] = useState({ annee: '', mois: '', semaine: '' });
  const [appliedFilters, setAppliedFilters] = useState({ annee: '', mois: '', semaine: '' });
  const [filterTree, setFilterTree] = useState({ years: [], speakers: [], locations: [] });
  const [allBroadcasts, setAllBroadcasts] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const filterKeyRef = useRef('');
  const hasAutoSelectedFirstRef = useRef(false);

  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    if (!detailId) return;
    let cancelled = false;
    setDetailLoading(true);
    getBroadcastById(detailId)
      .then(res => {
        if (cancelled) return;
        const raw = res.data?.data ?? res.data;
        if (!raw) throw new Error(`Broadcast ${detailId} was not returned by the API`);
        setSelectedBroadcast(normaliseBroadcast(raw));
      })
      .catch(error => {
        if (cancelled) return;
        setDetailError('Impossible de charger les détails du direct. La version disponible dans la liste est affichée.');
      })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [detailId]);

  const { data: liveData, loading: liveLoading } = useFetch(getLiveBroadcasts, null, []);
  const liveList = useMemo(() => (liveData?.data ?? []).map(normaliseBroadcast), [liveData]);
  const liveBroadcast = liveList.find(b => b.is_live) ?? liveList[0] ?? null;

  // Auto-select first broadcast on initial load only if it's live
  useEffect(() => {
    if (!hasAutoSelectedFirstRef.current && liveBroadcast && liveBroadcast.is_live) {
      hasAutoSelectedFirstRef.current = true;
      setSelectedBroadcast(liveBroadcast);
      setShowGrid(false);
      setDetailError('');
      setDetailLoading(true);
      setDetailId(liveBroadcast.id || liveBroadcast._id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [liveBroadcast]);

  useEffect(() => {
    getBroadcastFilterTree()
      .then(res => {
        setFilterTree(res.data?.data ?? { years: [], speakers: [], locations: [] });
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    const filterKey = JSON.stringify(appliedFilters);
    const isNewFilter = filterKey !== filterKeyRef.current;
    let cancelled = false;
    setGridLoading(true);

    getBroadcasts({ limit: LIMIT, page: 1, ...(appliedFilters.annee && { year: appliedFilters.annee }), ...(appliedFilters.mois && { month: appliedFilters.mois }), ...(appliedFilters.semaine && { week: appliedFilters.semaine }) })
      .then(res => {
        if (cancelled) return;
        const incoming = (res.data?.data ?? []).map(normaliseBroadcast);
        if (isNewFilter) { filterKeyRef.current = filterKey; setAllBroadcasts(incoming); }
        else setAllBroadcasts(prev => { const ids = new Set(prev.map(b => b.id)); return [...prev, ...incoming.filter(b => !ids.has(b.id))]; });
        setGridLoading(false);
      })
      .catch(() => { if (!cancelled) setGridLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(appliedFilters)]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasAppliedFilters = Object.values(appliedFilters).some(Boolean);
  const featuredBroadcast = hasAppliedFilters
    ? allBroadcasts[0] ?? null
    : liveBroadcast; // Only show live broadcasts by default, never fallback to non-live
  const featuredLoading = gridLoading || (!hasAppliedFilters && liveLoading);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    const empty = { annee: '', mois: '', semaine: '' };
    setFilters(empty); setAppliedFilters(empty);
    setAllBroadcasts([]);
    setShowGrid(true); setSelectedBroadcast(null); setDetailId(null); setDetailLoading(false); setDetailError('');
  }, []);

  const handleSidebarFilter = useCallback((newFilters) => {
    const { _video, _videoId, ...filterOnly } = newFilters;
    if (_video || _videoId) {
      const broadcastId = _videoId || _video?.id || _video?._id;
      setSelectedBroadcast(_video ? normaliseBroadcast(_video) : null);
      setShowGrid(false);
      setDetailError('');
      setDetailLoading(Boolean(broadcastId));
      setDetailId(broadcastId || null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const updated = { annee: '', mois: '', semaine: '', ...filterOnly };
    setFilters(updated); setAppliedFilters(updated);
    setAllBroadcasts([]);
    setShowGrid(true); setSelectedBroadcast(null); setDetailId(null); setDetailLoading(false); setDetailError('');
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="two-col">
      <BroadcastTreeSidebar
        filterTree={filterTree}
        filters={filters}
        selectedVideo={selectedBroadcast}
        onFilterSelect={handleSidebarFilter}
        onClearFilters={handleClear}
        className="video-sidebar"
      />

      <div className="main-col pt-3">
        {/* ── Content ─────────────────────────────────────────────────── */}
        {showGrid ? (
          !featuredLoading && !featuredBroadcast ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 500, padding: '40px 20px'
            }}>
              <div style={{
                background: '#fff', borderRadius: 12, padding: '40px 30px',
                textAlign: 'center', maxWidth: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <div style={{
                  width: 60, height: 60, background: '#f5e6e6', borderRadius: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <FiVideo size={28} color="#c91f2a" />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>
                  Aucun direct en cours
                </h3>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.5, marginBottom: 24 }}>
                  Il n'y a pas de diffusion en direct actuellement. Nous vous invitons à regarder les rediffusions de nos directs précédents ci-dessous.
                </p>
                <div style={{ fontSize: 12, color: '#999', marginTop: 20 }}>
                  Sélectionnez un direct dans le menu à gauche
                </div>
              </div>
            </div>
          ) : (
            <>
              {featuredLoading ? (
                <div style={{ paddingTop: 8 }}>
                  <SkeletonBox width="60%" height={22} style={{ marginBottom: 10 }} borderRadius={4} />
                  <SkeletonBox height={360} borderRadius={12} style={{ marginBottom: 16 }} />
                </div>
              ) : featuredBroadcast ? (
                <div style={{ maxWidth: 900, width: '100%', margin: '0 auto' }}>
                  {featuredBroadcast.is_live && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        background: '#e00', color: '#fff', fontSize: 11, fontWeight: 800,
                        letterSpacing: 1.5, textTransform: 'uppercase',
                        padding: '3px 10px', borderRadius: 4,
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
                        En direct
                      </span>
                    </div>
                  )}
                  {featuredBroadcast.theme && (
                    <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6, lineHeight: 1.3, color: '#1a1a2e' }}>
                      {featuredBroadcast.theme}
                    </h2>
                  )}
                  {featuredBroadcast.date && (
                    <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                      {fmtDate(featuredBroadcast.date)}
                    </p>
                  )}
                  <VideoPreview video={featuredBroadcast} />
                  <BroadcastMeta broadcast={featuredBroadcast} />
                </div>
              ) : null}
            </>
          )
        ) : detailLoading ? (
          /* Skeleton while fetching broadcast detail */
          <div style={{ paddingTop: 8 }}>
            <SkeletonBox width={140} height={14} borderRadius={4} style={{ marginBottom: 16 }} />
            <SkeletonBox width="60%" height={22} style={{ marginBottom: 10 }} borderRadius={4} />
            <SkeletonBox width="30%" height={13} style={{ marginBottom: 20 }} borderRadius={4} />
            <SkeletonBox height={360} borderRadius={12} style={{ marginBottom: 16 }} />
            <SkeletonBox width="80%" height={13} style={{ marginBottom: 8 }} borderRadius={4} />
            <SkeletonBox width="50%" height={13} borderRadius={4} />
          </div>

        ) : selectedBroadcast ? (
          <>
            {/* Live badge */}
            {detailError && (
              <p role="status" style={{ color: '#8b1512', fontSize: 13, marginBottom: 12 }}>
                {detailError}
              </p>
            )}
            {selectedBroadcast.is_live && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#e00', color: '#fff', fontSize: 11, fontWeight: 800,
                  letterSpacing: 1.5, textTransform: 'uppercase',
                  padding: '3px 10px', borderRadius: 4,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
                  En direct
                </span>
              </div>
            )}

            {selectedBroadcast.theme && (
              <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6, lineHeight: 1.3, color: '#1a1a2e' }}>
                {selectedBroadcast.theme}
              </h2>
            )}
            {selectedBroadcast.date && (
              <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                {fmtDate(selectedBroadcast.date)}
              </p>
            )}
            <VideoPreview video={selectedBroadcast} />
            <BroadcastMeta broadcast={selectedBroadcast} />
          </>

        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <FiVideo size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
            <p>Sélectionnez un direct pour le visionner.</p>
          </div>
        )}
      </div>
    </div>
  );
}
